'use client';

import Styles from '../order-review.module.scss';
import { TYPES } from '~/modules/payment/constants';
import { useSelectedPaymentOption, usePaymentReview } from '~/store/wmap-store';
import OrderModifyText from './order-modify-text';
import { formatCurrency } from '~/utils/utils';
import { IGetPricing } from '~/interfaces/commerce-types';
import Image from 'next/image';
import { Maybe } from '~/gql/__generated__/types';
interface Props {
  isConfirmationPage: boolean;
  userSelectedAmount?: number;
  Name: string;
  Street: string;
  StreetNumber?: string | null;
  City: string;
  stateZip: string;
  stateZipConfirmation: string;
  Email: string;
  formattedPhone: string;
  isBillingSameAsShipping: boolean;
  address?: string;
  apartment?: string;
  fullName?: string;
  cityStateZipFromPaymentPage?: string;
  cardLastFourDigit?: string;
  cardType?: string;
  storePhone: string;
  nationalSaleChat: boolean;
  progressiveDetails?: IGetPricing;
  selectedPaymentOption?: string;
  enableMSChat: boolean;
  affirmConfiguration?: Maybe<string>;
}

export default function OrderReviewClient(props: Props) {
  const selectedPaymentMethod = useSelectedPaymentOption();
  const paymentReview = usePaymentReview();

  const isProgressiveReview =
    (selectedPaymentMethod === TYPES.PROGRESIVE ||
      props?.selectedPaymentOption === TYPES.PROGRESIVE) &&
    paymentReview.review;

  const isProgressiveConfirmation =
    (selectedPaymentMethod === TYPES.PROGRESIVE ||
      props?.selectedPaymentOption === TYPES.PROGRESIVE) &&
    props.isConfirmationPage;
  const isAffirm =
    selectedPaymentMethod === TYPES.AFFIRM ||
    props?.selectedPaymentOption === TYPES.AFFIRM;
  return (
    <section
      className={`${Styles.orderReviewWrapper} ${props.isConfirmationPage ? Styles.confirmation : ''} ${
        isProgressiveReview && Styles.progressiveReview
      }`}
    >
      {!props.isConfirmationPage && !isProgressiveReview && (
        <h3 tabIndex={0}>Order Summary</h3>
      )}
      {props.isConfirmationPage && <h4 tabIndex={0}>Customer Info</h4>}

      <div className={Styles.infoWrapper}>
        {!props.isConfirmationPage && <h4 tabIndex={0}>Contact</h4>}
        <div
          className={`${Styles.col} ${isProgressiveReview && Styles.borderCol}`}
        >
          {props.Email && (
            <span data-cs-mask>
              <a href={`mailto:${props.Email}`} tabIndex={0}>
                {props.Email}
              </a>
            </span>
          )}
          {props.formattedPhone && (
            <span data-cs-mask>
              {!props.isConfirmationPage && props.Email && <> | </>}
              <a href={`tel:+${props.formattedPhone}`} tabIndex={0}>
                {props.formattedPhone}
              </a>
            </span>
          )}
        </div>

        <div className={Styles.col}>
          {props.isConfirmationPage ? (
            <div data-cs-mask>
              {props.Name && <span tabIndex={0}>{props.Name}</span>}
              <div>
                {props.StreetNumber && (
                  <span tabIndex={0}>{props.StreetNumber}</span>
                )}
                {props.Street && <span tabIndex={0}>{props.Street}</span>}
              </div>
              <div>
                {props.City && (
                  <span tabIndex={0}>
                    {props.City}
                    {', '}
                  </span>
                )}
                {props.stateZipConfirmation && (
                  <span tabIndex={0}>{props.stateZipConfirmation}</span>
                )}
              </div>
            </div>
          ) : (
            <>
              <h4 tabIndex={0}>Delivery</h4>
              <div data-cs-mask>
                {props.Name && (
                  <span tabIndex={0}>
                    {props.Name}
                    {', '}
                  </span>
                )}
                {props.Street && (
                  <span tabIndex={0}>
                    {props.Street}
                    {', '}
                  </span>
                )}
                {props.StreetNumber && (
                  <span tabIndex={0}>
                    {props.StreetNumber}
                    {', '}
                  </span>
                )}
                {props.City && (
                  <span tabIndex={0}>
                    {props.City}
                    {', '}
                  </span>
                )}
                {props.stateZip && (
                  <span className="d-sm-block" tabIndex={0}>
                    {props.stateZip}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {props.isConfirmationPage ? (
        <>
          <h4 tabIndex={0}>Payment Info</h4>
          {isAffirm && (
            <div className={Styles.affirmContent}>
              <Image
                src={'/makeapayment/icons/payment-connector/affirm.svg'}
                height={22}
                width={56}
                alt="Affirm Logo"
                tabIndex={0}
              />
              <div className={`${Styles.infoWrapper} mb-0`} tabIndex={0}>
                {props.affirmConfiguration}
              </div>
            </div>
          )}
          <div className={`${Styles.infoWrapper} mb-0`}>
            {!props.isBillingSameAsShipping && !isProgressiveConfirmation ? (
              <div className={Styles.col} data-cs-mask>
                <span tabIndex={0}>{props.fullName}</span>
                <span tabIndex={0}>{props.address}</span>
                {props.apartment && <span tabIndex={0}>{props.apartment}</span>}
                <span tabIndex={0}>{props.cityStateZipFromPaymentPage}</span>
              </div>
            ) : (
              <span tabIndex={0}>Billing Address the same as Delivery</span>
            )}
            <div className={Styles.col}>
              {isProgressiveReview || isProgressiveConfirmation ? (
                <>
                  <span tabIndex={0}>Progressive Leasing</span>
                  <span tabIndex={0}>
                    Lease ID:{' '}
                    {props.progressiveDetails?.LeaseDetails?.ApplicationId}
                  </span>
                </>
              ) : (
                !isAffirm && (
                  <div data-cs-mask>
                    <span tabIndex={0}>
                      {`Amount: ${formatCurrency(props.userSelectedAmount)}`}
                    </span>
                    <span tabIndex={0}>
                      {` ${props.cardType} ending in ${props.cardLastFourDigit}`}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </>
      ) : (
        <OrderModifyText
          storePhone={props.storePhone}
          nationalSaleChat={props.nationalSaleChat}
          paymentReview={paymentReview?.review}
          enableMSChat={props.enableMSChat}
        />
      )}
    </section>
  );
}
